import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, CaretLeft, CaretRight, Globe, MapPin, Phone, Star } from "@phosphor-icons/react";
import api from "../lib/api";

export default function LocaleDettaglio() {
  const { rid } = useParams();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(false);
  const [slide, setSlide] = useState(0);
  const touchX = useRef(null);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e, total) => {
    if (touchX.current == null || total < 2) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) setSlide((s) => (s + (dx < 0 ? 1 : -1) + total) % total);
    touchX.current = null;
  };

  useEffect(() => {
    api.get(`/marketplace/locali/${rid}`).then((r) => setL(r.data)).catch(() => setErr(true));
  }, [rid]);

  useEffect(() => {
    if (!l) return undefined;
    document.title = `${l.nome}${l.citta ? ` — ${l.tipologia || "Locale"} a ${l.citta}` : ""} | Dishenda`;
    const ld = {
      "@context": "https://schema.org", "@type": "Restaurant", name: l.nome,
      servesCuisine: l.tipologia || undefined,
      telephone: l.telefono || undefined, url: window.location.href,
      image: l.foto_url || undefined, priceRange: l.fascia_prezzo || undefined,
      address: (l.indirizzo || l.citta) ? {
        "@type": "PostalAddress", streetAddress: l.indirizzo || undefined,
        addressLocality: l.citta || undefined, addressRegion: l.provincia || undefined, addressCountry: "IT",
      } : undefined,
      aggregateRating: l.rating_avg ? {
        "@type": "AggregateRating", ratingValue: l.rating_avg, reviewCount: l.rating_count,
      } : undefined,
    };
    const s = document.createElement("script");
    s.type = "application/ld+json"; s.id = "mk-jsonld"; s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => { document.getElementById("mk-jsonld")?.remove(); };
  }, [l]);

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 flex flex-col items-center justify-center gap-4" data-testid="mkd-404">
        <p className="font-display text-2xl font-bold">Locale non trovato</p>
        <Link to="/locali" className="text-sm text-[#2ce0cb] font-bold hover:underline" data-testid="mkd-back-404">← Torna alla vetrina</Link>
      </div>
    );
  }
  if (!l) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center text-sm" data-testid="mkd-loading">Caricamento…</div>;

  const photos = [...new Set([l.foto_url, ...(l.galleria || [])].filter(Boolean))];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50" data-testid="mkd-page">
      <nav className="fixed top-0 inset-x-0 z-40 bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
          <Link to="/locali" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100 transition-colors" data-testid="mkd-back">
            <ArrowLeft size={16} weight="bold" /> Tutti i locali
          </Link>
          <img src="/vetrina/logo-dishenda.png" alt="Dishenda" className="h-12" />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-5 pt-28 pb-16">
        {photos.length > 0 ? (
          <div className="relative touch-pan-y" data-testid="mkd-carousel"
            onTouchStart={onTouchStart} onTouchEnd={(e) => onTouchEnd(e, photos.length)}>
            <img src={photos[Math.min(slide, photos.length - 1)]} alt={l.nome} className="w-full h-64 sm:h-80 object-cover rounded-2xl border border-zinc-800" data-testid="mkd-foto" />
            {photos.length > 1 && (
              <>
                <button onClick={() => setSlide((s) => (s - 1 + photos.length) % photos.length)} data-testid="mkd-prev" aria-label="Foto precedente"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-zinc-950/70 backdrop-blur border border-zinc-700 flex items-center justify-center hover:bg-zinc-800 transition-colors">
                  <CaretLeft size={16} weight="bold" />
                </button>
                <button onClick={() => setSlide((s) => (s + 1) % photos.length)} data-testid="mkd-next" aria-label="Foto successiva"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-zinc-950/70 backdrop-blur border border-zinc-700 flex items-center justify-center hover:bg-zinc-800 transition-colors">
                  <CaretRight size={16} weight="bold" />
                </button>
                <span className="absolute top-3 right-3 text-[10px] font-mono bg-zinc-950/70 backdrop-blur px-2 py-0.5 rounded-full text-zinc-300" data-testid="mkd-counter">
                  {Math.min(slide, photos.length - 1) + 1}/{photos.length}
                </span>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {photos.map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === Math.min(slide, photos.length - 1) ? "bg-[#2ce0cb]" : "bg-zinc-600"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="w-full h-48 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
            <img src="/vetrina/logo-glyph.png" alt="" className="w-24 opacity-25" />
          </div>
        )}

        <div className="flex items-start justify-between gap-4 flex-wrap mt-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter" data-testid="mkd-nome">{l.nome}</h1>
            <p className="text-sm text-zinc-500 mt-2 flex items-center gap-1.5" data-testid="mkd-zona">
              <MapPin size={15} weight="duotone" className="text-[#2ce0cb]" />
              {[l.indirizzo, l.citta, l.provincia].filter(Boolean).join(", ") || "Italia"}
              {l.tipologia ? ` · ${l.tipologia}` : ""}{l.fascia_prezzo ? ` · ${l.fascia_prezzo}` : ""}
            </p>
          </div>
          {l.rating_avg && (
            <span className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-lg font-bold text-amber-400" data-testid="mkd-rating">
              <Star size={18} weight="fill" /> {l.rating_avg}
              <span className="text-zinc-500 font-normal text-xs">({l.rating_count} verificate)</span>
            </span>
          )}
        </div>

        {l.descrizione && <p className="text-sm text-zinc-400 leading-relaxed mt-5 max-w-2xl" data-testid="mkd-desc">{l.descrizione}</p>}

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 mt-8 items-start">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6" data-testid="mkd-menu">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Dal menu</p>
            {l.menu_preview?.length ? (
              <div className="space-y-2 text-sm">
                {l.menu_preview.map((d) => (
                  <div key={d.nome} className="flex justify-between gap-4">
                    <span className="text-zinc-200">{d.nome}</span>
                    <span className="text-zinc-500 font-mono">€{Number(d.prezzo || 0).toFixed(2).replace(".", ",")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">Menu in aggiornamento.</p>
            )}
            <a href={l.menu_url} className="inline-block text-xs text-[#2ce0cb] font-bold mt-4 hover:underline" data-testid="mkd-menu-link">
              Vedi il menu completo →
            </a>
          </div>

          <div className="bg-zinc-900 border border-[#2ce0cb]/40 rounded-2xl p-6 lg:sticky lg:top-24" data-testid="mkd-booking">
            <p className="font-display text-lg font-bold tracking-tight">Prenota il tuo tavolo</p>
            <p className="text-xs text-zinc-500 mt-1">Gratis · conferma immediata · nessuna app da scaricare</p>
            {l.tavoli_stasera > 0 && (
              <span className={`inline-block mt-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${l.tavoli_stasera <= 3 ? "bg-amber-400/10 text-amber-400 border-amber-400/30" : "bg-[#2ce0cb]/10 text-[#2ce0cb] border-[#2ce0cb]/30"}`} data-testid="mkd-live">
                {l.tavoli_stasera <= 3 ? `Ultimi ${l.tavoli_stasera} slot stasera` : "Tavoli liberi stasera"}
              </span>
            )}
            <a href={l.booking_url} data-testid="mkd-book-btn"
              className="mt-5 w-full h-12 rounded-full bg-[#2ce0cb] text-zinc-950 text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#6ef0e0] active:scale-[0.98] transition-[background-color,transform]">
              <CalendarCheck size={18} weight="bold" /> Prenota gratis
            </a>
            <div className="mt-4 space-y-2 text-xs text-zinc-500">
              {l.telefono && (
                <a href={`tel:${l.telefono}`} className="flex items-center gap-2 hover:text-zinc-300 transition-colors" data-testid="mkd-tel">
                  <Phone size={14} weight="duotone" className="text-[#2ce0cb]" /> {l.telefono}
                </a>
              )}
              {l.sito_web && (
                <a href={l.sito_web} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-zinc-300 transition-colors" data-testid="mkd-web">
                  <Globe size={14} weight="duotone" className="text-[#2ce0cb]" /> Sito web
                </a>
              )}
            </div>
            {l.orari?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="mkd-orari">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Orari di apertura</p>
                <div className="space-y-1">
                  {l.orari.map((o) => (
                    <div key={o.giorno} className="flex justify-between text-xs">
                      <span className="text-zinc-400">{o.giorno}</span>
                      <span className={o.aperto ? "text-zinc-200 font-mono" : "text-zinc-600"}>
                        {o.aperto ? o.turni.join(" · ") : "Chiuso"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {l.eventi?.length > 0 && (
          <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-2xl p-6" data-testid="mkd-eventi">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Eventi in programma</p>
            <div className="space-y-2">
              {l.eventi.map((e) => (
                <a key={e.url} href={e.url} data-testid={`mkd-evento-${e.url.split("/").pop()}`}
                  className="flex items-center justify-between gap-4 bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 hover:border-zinc-600 transition-colors group">
                  <div>
                    <p className="text-sm font-bold text-zinc-100 group-hover:text-[#2ce0cb] transition-colors">{e.nome}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {new Date(`${e.data}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                      {e.ora_da ? ` · ore ${e.ora_da}` : ""}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-[#2ce0cb] shrink-0">Prenota →</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="border-t border-zinc-900 py-10 text-center text-xs text-zinc-500">
        © 2026 Dishenda · Prenotazioni senza commissioni · <Link to="/locali" className="underline hover:text-zinc-300">Tutti i locali</Link>
      </footer>
    </div>
  );
}
